/**
 * En-tête d'écran — **toujours visible**, quel que soit le défilement.
 *
 * Pendant du `TabBar` : la barre d'onglets tient le bas de l'écran, cet en-tête
 * tient le haut, et le contenu défile entre les deux. C'est le même matériau
 * que la barre — une surface **pleine** depuis la refonte du 25/08/2026 —,
 * décrit dans `styles/surfaces.css`.
 *
 * ⚠️ **`position: sticky`, pas `fixed`.** L'en-tête vit dans le flux défilant et
 * s'y colle en haut. En `fixed` il faudrait réserver sa hauteur à la main dans
 * chaque écran, et sa largeur ne suivrait plus la colonne de 640 px sur grand
 * écran. En `sticky`, il n'y a rien à synchroniser.
 *
 * ⚠️ L'en-tête **déborde** le rembourrage latéral de `.app-main` par une marge
 * négative, pour que le verre aille d'un bord à l'autre. Sans cela on verrait
 * deux bandes de contenu non flouté défiler de chaque côté.
 *
 * ⚠️ Il absorbe `env(safe-area-inset-top)` : en PWA installée, il n'y a plus de
 * barre de navigateur au-dessus, et sans cela le titre passerait sous l'heure et
 * l'îlot dynamique.
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { IconSettings } from './icons';
import { SyncBadge } from './SyncBadge';

/**
 * 🔴 **L'accès aux Réglages est ICI, en haut à droite — plus dans la barre
 * d'onglets** *(29/08/2026, après usage sur l'appareil)*.
 *
 * Les specs §8.2 en faisaient un onglet, et la V1 l'a servi ainsi pendant dix
 * jours. Ce qui a changé, c'est le nombre de voisins : à cinq onglets, la
 * rangée devenait serrée et **trois libellés sur cinq étaient tronqués**. Or
 * Réglages est, de très loin, l'onglet le moins ouvert — il ne contient que du
 * réglage, par définition rare. Lui laisser un cinquième de la barre revenait à
 * faire payer les quatre gestes quotidiens pour un geste mensuel.
 *
 * ⚠️ **En haut à droite plutôt qu'en bas** : c'est la place conventionnelle
 * d'un accès secondaire sur iOS, et surtout la seule qui n'entre en concurrence
 * avec rien — la barre du bas est réservée à ce qu'on fait tous les jours.
 *
 * ⚠️ **Posé ici, en un seul endroit**, comme la pastille de synchronisation
 * (V2 §3.5) : tous les écrans passent par cet en-tête. L'y mettre garantit
 * qu'aucun ne l'oublie et qu'aucun ne le place différemment.
 */
function SettingsLink() {
  const { pathname } = useLocation();

  /*
   * ⚠️ **Rien à afficher quand on y est déjà.** Un lien vers la page qu'on
   * regarde n'est pas seulement inutile : il occupe la place où l'on cherchera
   * le bouton de retour des sous-pages. Le préfixe couvre `/reglages/theme` et
   * consorts.
   */
  if (pathname === '/reglages' || pathname.startsWith('/reglages/')) return null;

  return (
    <Link to="/reglages" aria-label="Réglages" className="screen-header__action">
      <IconSettings />
    </Link>
  );
}

export function ScreenHeader({ children }: { children: ReactNode }) {
  return (
    <header className="screen-header">
      <div className="screen-header__row">
        <div className="screen-header__main">{children}</div>
        {/*
          ⚠️ La pastille avant l'engrenage : elle n'apparaît que lorsque la file
          n'est pas vide, donc la placer en dernier ferait sauter l'engrenage
          d'un cran à chaque saisie hors ligne. Un contrôle qui se déplace est un
          contrôle qu'on rate.
        */}
        <SyncBadge />
        <SettingsLink />
      </div>
    </header>
  );
}

/** Titre d'écran simple, pour les onglets qui n'ont pas de navigation propre. */
export function ScreenTitle({ children }: { children: ReactNode }) {
  return (
    <ScreenHeader>
      <h1 className="screen-header__title">{children}</h1>
    </ScreenHeader>
  );
}
